import React, {
  useCallback,
  useState,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import {
  Autocomplete,
  TextField,
  Box,
  Typography,
  CircularProgress,
  ToggleButton,
  ToggleButtonGroup,
  Divider,
  IconButton,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { apiCall } from "./api";
import { getDistanceFromLatLng } from "./utils/geo";

const SearchBar = forwardRef(({
  isAdmin,
  onSearchSelect,
  clearSidePanel,
  user,
  project,
  year,
  mapBounds,
  mapCenter,
  mapRef: externalMapRef,
}, ref) => {
  const [searchType, setSearchType] = useState("place"); // "place" or "keyword"
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showSearch, setShowSearch] = useState(true);
  const [hasSearched, setHasSearched] = useState(false);

  // Monotonic request id for keyword search. Every keyword fetch captures
  // the current value; the async handler only commits its results if it's
  // still the latest request. Without this, keystroke-triggered requests
  // race — a slow response for "30t" could land after "30th stree" and
  // overwrite the fresh results with stale ones (client bug: keyword
  // results don't match the typed query). Address search uses Google's
  // session-token API and isn't affected, matching the client's report.
  const keywordReqSeq = useRef(0);
  // Debounce timer so we don't fire a request on every keystroke.
  const debounceTimer = useRef(null);

  useEffect(
    () => () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    },
    [],
  );

  useImperativeHandle(ref, () => ({
    clear: () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      keywordReqSeq.current++;
      setInputValue("");
      setOptions([]);
      setSelectedLocation(null);
      setHasSearched(false);
      setShowSearch(true);
      setLoading(false);
    },
  }));

  const fetchAddressSuggestions = useCallback(
    (value) => {
      if (!window.google || !window.google.maps || !window.google.maps.places) {
        console.error("Google Maps API not loaded");
        setOptions([]);
        setLoading(false);
        return;
      }

      const { AutocompleteSuggestion } = window.google.maps.places;
      const sessionToken =
        new window.google.maps.places.AutocompleteSessionToken();

      const request = {
        input: value,
        sessionToken: sessionToken,
      };

      const currentBounds = externalMapRef?.current?.getBounds?.();
      if (currentBounds) {
        const sw = currentBounds.getSouthWest();
        const ne = currentBounds.getNorthEast();
        const latPad = (ne.lat - sw.lat) * 0.5;
        const lngPad = (ne.lng - sw.lng) * 0.5;
        request.locationRestriction = new window.google.maps.LatLngBounds(
          { lat: sw.lat - latPad, lng: sw.lng - lngPad },
          { lat: ne.lat + latPad, lng: ne.lng + lngPad }
        );
      }

      AutocompleteSuggestion.fetchAutocompleteSuggestions(request)
        .then((response) => {
          if (response.suggestions) {
            setOptions(
              response.suggestions.map((suggestion) => ({
                place_id: suggestion.placePrediction.placeId,
                description: suggestion.placePrediction.text.text,
              }))
            );
          } else {
            setOptions([]);
          }
          setLoading(false);
        })
        .catch((error) => {
          console.log("Error fetching autocomplete suggestions:", error);
          setOptions([]);
          setLoading(false);
        });
    },
    [externalMapRef]
  );

  const fetchKeywordSuggestions = useCallback(
    (value) => {
      // Claim the latest request slot. A response is only applied below if
      // its captured id still matches — so out-of-order responses from
      // earlier keystrokes are dropped instead of clobbering fresh results.
      const reqId = ++keywordReqSeq.current;
      const isStale = () => reqId !== keywordReqSeq.current;

      // Auth refactor 2026-05-27: dropped client-supplied uid. The server
      // derives the viewer uid from the verified Firebase ID token via
      // apiCall(); anonymous viewers get the public-only search path.
      apiCall("searchStories.php", {
        query: {
          projectsid: project.projectsid,
          type: "keyword",
          query: value,
        },
      })
        .then((data) => {
          if (isStale()) return;
          const results = data?.results || [];

          const centerLat = mapCenter?.lat ?? null;
          const centerLng = mapCenter?.lng ?? null;

          const withDistance = results.map((item) => {
            if (centerLat != null && centerLng != null && item.loc_id) {
              const [lat, lng] = item.loc_id.split(",").map(Number);
              if (!isNaN(lat) && !isNaN(lng)) {
                return { ...item, _dist: getDistanceFromLatLng(centerLat, centerLng, lat, lng) };
              }
            }
            return { ...item, _dist: Infinity };
          });

          withDistance.sort((a, b) => {
            const aIsCurrent = a.year === year;
            const bIsCurrent = b.year === year;
            if (aIsCurrent && !bIsCurrent) return -1;
            if (!aIsCurrent && bIsCurrent) return 1;
            return a._dist - b._dist;
          });

          setOptions(withDistance);
          setLoading(false);
        })
        .catch((error) => {
          if (isStale()) return;
          console.log("Error fetching keyword suggestions:", error);
          setOptions([]);
          setLoading(false);
        });
    },
    [project.projectsid, year, mapCenter]
  );

  // Fetch autocomplete suggestions. Typing is debounced (~250ms) so we
  // don't fire a request per keystroke; Enter fetches immediately.
  const fetchSuggestions = useCallback(
    (value, searchTypeParam, immediate = false) => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      if (!value || value.length < 3) {
        // Invalidate any in-flight keyword response so a late arrival
        // can't repopulate results the user just cleared below 3 chars.
        keywordReqSeq.current++;
        setOptions([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const run = () => {
        try {
          if (searchTypeParam === "place") {
            fetchAddressSuggestions(value);
          } else {
            fetchKeywordSuggestions(value);
          }
        } catch (error) {
          console.error("Error fetching autocomplete suggestions:", error);
          setOptions([]);
          setLoading(false);
        }
      };
      if (immediate) {
        run();
      } else {
        debounceTimer.current = setTimeout(run, 250);
      }
    },
    [fetchAddressSuggestions, fetchKeywordSuggestions]
  );

  // Handle Enter key to fetch suggestions
  const handleKeyDown = useCallback(
    (event) => {
      // Enter confirms IME composition for many languages; it must not also
      // submit a keyword search. keyCode 229 covers older browser events.
      const isComposing =
        event.isComposing || event.nativeEvent?.isComposing || event.keyCode === 229;
      if (event.key === "Enter" && !isComposing) {
        event.preventDefault();
        setHasSearched(true);
        fetchSuggestions(inputValue, searchType, true);
      }
    },
    [inputValue, searchType, fetchSuggestions]
  );

  // Handle selection from suggestions
  const handleSelect = useCallback(
    (event, value) => {
      if (!value) return;
      clearSidePanel?.();
      setHasSearched(false);

      if (searchType === "place") {
        // For Google Places, fetch place details using Place API (recommended)
        if (window.google && window.google.maps && window.google.maps.places) {
          const { Place } = window.google.maps.places;
          const place = new Place({
            id: value.place_id,
          });

          place
            .fetchFields({
              fields: ["formattedAddress", "location"],
            })
            .then(() => {
              if (place.formattedAddress && place.location) {
                const formatted_address = place.formattedAddress;
                const { lat, lng } = place.location;
                setSelectedLocation(formatted_address);
                setShowSearch(false);
                setInputValue(formatted_address);
                onSearchSelect(null, {
                  address: formatted_address,
                  lat: lat(),
                  lng: lng(),
                  year: null,
                  id: null,
                });
              } else {
                console.error("Error fetching place details: missing data");
              }
            })
            .catch((error) => {
              console.error("Error fetching place details:", error);
            });
        } else {
          console.error("Google Maps API not loaded");
        }
      } else {
        // For keyword search, use existing logic
        const addressField = value.fields?.find(
          (f) => f.field_name === "Address"
        );
        const placeNameField = value.fields?.find(
          (f) => f.field_name === "Place Name"
        );
        const address = addressField?.field_value || "";
        const placeName = placeNameField?.field_value || "";
        setSelectedLocation(placeName || address);
        setShowSearch(false);
        setInputValue(placeName || address);
        const [lat, lng] = value.loc_id
          ? value.loc_id.split(",").map(Number)
          : [null, null];

        if (lat !== null && lng !== null) {
          onSearchSelect(null, {
            address,
            lat,
            lng,
            year: value.year || null,
            id: value.id || null,
          });
        }
      }
    },

    [clearSidePanel, onSearchSelect, searchType]
  );

  // Handle search type toggle
  const handleSearchTypeChange = (event, newType) => {
    if (newType !== null) {
      // Drop any in-flight/debounced keyword request so results from the
      // previous mode can't land in the newly-selected one.
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
      keywordReqSeq.current++;
      setSearchType(newType);
      setOptions([]); // Clear previous results
      setLoading(false); // Stop any loading
      setHasSearched(false);
    }
  };

  // Determine placeholder based on search type
  const getPlaceholder = () => {
    return searchType === "place" ? "Search Area" : "Search Keyword";
  };

  // Get option label based on search type
  const getOptionLabel = (option) => {
    if (typeof option === "string") return option;

    if (searchType === "place") {
      // For Google Places, use description
      return option.description || "";
    } else {
      // For keyword search, prefer Title, then Place Name
      const placeNameField = option.fields?.find(
        (f) => f.field_name === "Place Name"
      );
      const titleField = option.fields?.find((f) => f.field_name === "Title");
      return titleField?.field_value || placeNameField?.field_value || "";
    }
  };

  const getYearLabel = (yearValue) => {
    const numericYear = Number(yearValue);
    if (Number.isFinite(numericYear) && numericYear > 2020) {
      return "Vision";
    }
    if (Number.isFinite(numericYear)) {
      return `${numericYear}s`;
    }
    return "";
  };

  // Render custom option. MUI includes React's special `key` in props; pull
  // it out before spreading so React does not warn and list identity remains
  // stable while fast keyword responses reorder.
  const renderOption = (props, option, { index }) => {
    const { key: optionKey, ...optionProps } = props;
    if (searchType === "place") {
      // For Google Places, render description
      return (
        <React.Fragment key={optionKey || option?.place_id}>
          <li {...optionProps} style={{ display: "block" }}>
            <Box
              sx={{
                py: 0.5,
                display: "flex",
                alignItems: "center",
                gap: "11px",
              }}
            >
              <img
                src={"./location.png"}
                alt="location"
                style={{ height: "16px", width: "12px" }}
              />
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "700",
                  fontSize: "0.82rem",
                  color: "var(--theme-color)",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {option.description}
              </Typography>
            </Box>
          </li>{" "}
          {index !== options.length - 1 && <Divider />}
        </React.Fragment>
      );
    }

    // For keyword search, show place name in bold and matching content
    const placeNameField = option.fields?.find(
      (f) => f.field_name === "Place Name"
    );
    const placeName = placeNameField?.field_value || "";

    // Search query (lowercase for case-insensitive matching)
    const searchQuery = inputValue.toLowerCase();

    // Find the field that contains matching text by iterating through all fields
    let matchingField = null;
    let matchIndex = -1;
    const excludeFields = ["Address", "Place Name"]; // Fields to exclude from search

    if (option.fields && Array.isArray(option.fields)) {
      for (const field of option.fields) {
        // Skip excluded fields and fields without values or non-text fields
        if (
          excludeFields.includes(field.field_name) ||
          !field.field_value ||
          field.field_type === "file"
        ) {
          continue;
        }

        const fieldText = field.field_value.toString().toLowerCase();
        const index = fieldText.indexOf(searchQuery);

        if (index !== -1) {
          matchingField = field;
          matchIndex = index;
          break; // Stop at first match
        }
      }
    }

    let displayText = "";

    if (matchingField && matchIndex !== -1) {
      // Extract text around the match
      const fullText = matchingField.field_value;
      const contextChars = 75; // Characters before and after the match

      let startIndex = Math.max(0, matchIndex - contextChars);
      let endIndex = Math.min(
        fullText.length,
        matchIndex + searchQuery.length + contextChars
      );

      // Expand to word boundaries
      while (startIndex > 0 && /\S/.test(fullText[startIndex - 1])) {
        startIndex--;
      }
      while (endIndex < fullText.length && /\S/.test(fullText[endIndex])) {
        endIndex++;
      }

      const extractedText = fullText.substring(startIndex, endIndex);

      // Add ellipses
      const hasBefore = startIndex > 0;
      const hasAfter = endIndex < fullText.length;
      displayText =
        (hasBefore ? "..." : "") + extractedText + (hasAfter ? "..." : "");
    } else {
      // Fallback to just showing first few characters of any text field
      if (option.fields && Array.isArray(option.fields)) {
        for (const field of option.fields) {
          if (field.field_value && field.field_type !== "file") {
            const fieldValue = field.field_value.toString();
            displayText =
              fieldValue.length > 150
                ? fieldValue.substring(0, 150) + "..."
                : fieldValue;
            break;
          }
        }
      }
    }

    return (
      <React.Fragment key={optionKey || option?.id}>
        <li {...optionProps} style={{ display: "block" }}>
          <Box
            sx={{
              display: "flex",
              gap: "11px",
              justifyContent: "space-between",
              alignItems: "flex-start",
            }}
          >
            <Box sx={{ display: "flex", gap: "11px" }}>
              <img
                src={"./location.png"}
                alt="location"
                style={{ height: "16px", width: "12px", marginTop: "5px" }}
              />
              <Box>
                <Box
                  sx={{
                    color: "var(--theme-color)",
                    fontWeight: "700",
                    fontSize: "0.975rem",
                  }}
                >
                  {placeName}
                </Box>
                <Box
                  sx={{
                    fontSize: "0.975rem",
                    fontWeight: "400",
                    color: "var(--theme-color)",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {displayText}
                </Box>
              </Box>
            </Box>
            <Typography
              sx={{
                fontSize: "0.875rem",
                fontWeight: "600",
                color: "var(--theme-color)",
                alignSelf: "flex-start",
                marginTop: "5px",
              }}
            >
              {getYearLabel(option.year)}
            </Typography>
          </Box>
        </li>
        {index !== options.length - 1 && <Divider />}
      </React.Fragment>
    );
  };

  // Common rendering function
  const renderSearchInput = (params, isAdminView = false) => (
    <TextField
      {...params}
      value={inputValue}
      placeholder={getPlaceholder()}
      size="small"
      autoFocus={isAdminView}
      onKeyDown={handleKeyDown}
      sx={{
        "& .MuiFormControl-root": {
          background: "transparent",
        },
        "& .MuiOutlinedInput-notchedOutline": {
          border: "none",
          outline: "none",
        },
        "& .MuiInputBase-input": {
          fontSize: "0.813rem",
          fontWeight: 700,
          backgroundColor: "transparent",
          paddingLeft: "0 !important",
          "&::placeholder": {
            fontSize: "1.2rem",
            opacity: 0.5,
          },
        },
      }}
      InputProps={{
        ...params.InputProps,
        endAdornment: (
          <>
            {loading && <CircularProgress size={20} />}
            {inputValue && (
              <IconButton
                size="small"
                onClick={() => {
                  if (debounceTimer.current) {
                    clearTimeout(debounceTimer.current);
                    debounceTimer.current = null;
                  }
                  keywordReqSeq.current++;
                  setInputValue("");
                  setOptions([]);
                  setHasSearched(false);
                  setSelectedLocation(null);
                  setLoading(false);
                  clearSidePanel?.();
                }}
                sx={{ p: 0.25 }}
              >
                <ClearIcon sx={{ fontSize: "1rem" }} />
              </IconButton>
            )}
            {params.InputProps.endAdornment}
          </>
        ),
      }}
    />
  );

  const handleBoxClick = () => {
    // When user clicks the selected location box → show input again
    setShowSearch(true);
  };

  return (
    <Box sx={{ top: 10, left: 10, zIndex: 1000 }}>
      <Box sx={{ display: "flex", flexDirection: "column" }}>
        {/* 📍 Display Selected Location OR Search UI */}
        {!showSearch && selectedLocation ? (
          <>
            <Typography
              variant="h5"
              sx={{
                color: "var(--theme-color)",
                fontSize: "12px",
                textTransform: "uppercase",
              }}
            >
              Location
            </Typography>
            <Typography
              variant="h6"
              onClick={handleBoxClick}
              sx={{
                cursor: "pointer",
                color: "var(--theme-color)",
                fontSize: "13px",
                fontWeight: "700",
                textTransform: "uppercase",
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {selectedLocation}
            </Typography>
          </>
        ) : (
          <>
            <ToggleButtonGroup
              value={searchType}
              exclusive
              onChange={handleSearchTypeChange}
              size="small"
              fullWidth
              sx={{
                width: "max-content",
                "& .MuiToggleButton-root": {
                  color: "var(--theme-color) !important",
                  fontSize: "0.75rem",
                  fontWeight: 500,
                  textTransform: "none",
                  border: "none",
                  justifyContent: "start",
                  flex: 1,
                  py: 0,
                  "&.Mui-selected": {
                    background: "unset",
                    fontWeight: "900 !important",
                  },
                  "&:not(.Mui-selected)": {
                    color: "var(--theme-color) !important",
                  },
                  "&:hover": {
                    backgroundColor: "unset",
                  },
                },
              }}
            >
              <ToggleButton value="keyword">KEYWORD</ToggleButton>
              <ToggleButton value="place">ADDRESS</ToggleButton>
            </ToggleButtonGroup>

            {/* 🔍 Autocomplete Input */}
            <Autocomplete
              key={searchType}
              clearOnEscape
              popupIcon={null}
              inputValue={inputValue}
              options={options}
              loading={loading}
              getOptionLabel={getOptionLabel}
              renderOption={renderOption}
              onInputChange={(e, value, reason) => {
                if (reason === "input") {
                  setInputValue(value);
                  if (value && value.length >= 3) {
                    fetchSuggestions(value, searchType);
                  } else {
                    if (debounceTimer.current) {
                      clearTimeout(debounceTimer.current);
                      debounceTimer.current = null;
                    }
                    keywordReqSeq.current++;
                    setHasSearched(false);
                    setOptions([]);
                    setLoading(false);
                  }
                }
              }}
              onChange={handleSelect}
              renderInput={(params) => renderSearchInput(params, !!user)}
              filterOptions={(options) => options}
              noOptionsText={hasSearched ? "No results found" : ""}
              ListboxProps={{
                style: {
                  maxHeight: "300px",
                  overflowY: "auto",
                },
              }}
              open={
                (options.length > 0 && inputValue.length > 0) ||
                (hasSearched && inputValue.length > 0)
              }
              slotProps={{
                popper: {
                  placement: "bottom-start",
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [-7, 20],
                      },
                    },
                  ],
                  sx: {
                    width: { xs: "90vw !important", md: "354px !important" },

                    "& .MuiAutocomplete-option": {
                      fontSize: "0.813rem",
                      "&:hover": {
                        backgroundColor: "var(--theme-bg-clr) !important",
                      },
                    },
                  },
                },
              }}
            />
          </>
        )}
      </Box>
    </Box>
  );
});

export default SearchBar;
